import React, { FC, useEffect, useRef, useState } from 'react'
import ReactDom from 'react-dom'
import { useIsMobile } from 'src/hooks/useWindowSize'

import { FORM_ELEMENTS } from '../types'

import * as SC from './styled'

export type HighlightSelectorProps = {
  className?: string
  type?: FORM_ELEMENTS.HIGHLIGHT_SELECTOR
  backgroundColor?: string
  color?: string
  value: number
  optionsTitle: string
  options: {
    value: number
    text: string
  }[]
  onOptionClick: (value: number) => void
}

const OptionsWrapper: FC<
  HighlightSelectorProps & {
    isOpen: boolean
    optionsRef: React.RefObject<HTMLDivElement>
  }
> = ({
  isOpen,
  optionsRef,
  backgroundColor,
  color,
  optionsTitle,
  options,
  onOptionClick,
}) => (
  <SC.OptionsWrapper
    backgroundColor={backgroundColor}
    color={color}
    $isOpened={isOpen}
    ref={optionsRef}
  >
    <SC.OptionsTitle>{optionsTitle}</SC.OptionsTitle>
    {options.map((option, index) => (
      <SC.OptionWrapper key={index}>
        <SC.Option onClick={() => onOptionClick(option.value)}>
          {option.text}
        </SC.Option>
      </SC.OptionWrapper>
    ))}
  </SC.OptionsWrapper>
)

const HighlightSelector: FC<HighlightSelectorProps> = (props) => {
  const { className, backgroundColor, color, value, options } = props
  const isMobile = useIsMobile()
  const wrapperRef = useRef<any>()
  const optionsRef = useRef<any>()
  const [isOpen, setIsOpen] = useState(false)

  const displayOptions = () => {
    if (
      typeof window !== 'undefined' &&
      wrapperRef.current &&
      optionsRef.current
    ) {
      const { top, left, width } = wrapperRef.current.getBoundingClientRect()
      optionsRef.current.style.top = `${
        top + wrapperRef.current.offsetHeight
      }px`
      optionsRef.current.style.left = `${left}px`
      optionsRef.current.style.width = `${width}px`
    }
  }

  const handleContainerClick = () => {
    setIsOpen(!isOpen)
    if (!isMobile) {
      displayOptions()
    }
  }

  useEffect(() => {
    if (!isMobile) {
      document.addEventListener('scroll', displayOptions)
    }
    return () => {
      document.removeEventListener('scroll', displayOptions)
    }
  })

  const reactPortal =
    typeof window !== 'undefined'
      ? document.getElementById('react-portal-container')
      : null

  return (
    <SC.Container className={className} onClick={handleContainerClick}>
      <SC.Wrapper ref={wrapperRef}>
        <SC.CustomHighlight backgroundColor={backgroundColor} color={color}>
          {options[value ?? 0]?.text ?? ''}
          <SC.CustomIcon icon="chevronDown" />
        </SC.CustomHighlight>
        {isMobile ? (
          <OptionsWrapper {...props} isOpen={isOpen} optionsRef={optionsRef} />
        ) : (
          typeof window !== 'undefined' &&
          reactPortal &&
          ReactDom.createPortal(
            <OptionsWrapper
              {...props}
              isOpen={isOpen}
              optionsRef={optionsRef}
            />,
            reactPortal
          )
        )}
      </SC.Wrapper>
    </SC.Container>
  )
}

export default HighlightSelector
